import { createFileRoute, Link } from "@tanstack/react-router";
import { AccountShell } from "@/components/customer/AccountShell";
import { useCustomerSubscription, subscriptionErrorMessage } from "@/hooks/useCustomerSubscriptions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { 
  ArrowLeft, 
  ChevronLeft, 
  ChevronRight,
  Info,
  CheckCircle2,
  Clock,
  XCircle,
  SkipForward,
  CalendarDays
} from "lucide-react";
import { formatISTDate } from "@/lib/date/ist";
import { cn } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import { format, isSameDay, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";

export const Route = createFileRoute("/_customer/account/subscriptions/$subscriptionId/calendar")({
  component: SubscriptionCalendarPage,
  head: () => ({
    meta: [
      { title: "Delivery Calendar | Poonam's Healthy Kitchen" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type DeliveryStatus = "scheduled" | "delivered" | "skipped" | "rescheduled" | "cancelled";

function SubscriptionCalendarPage() {
  const { subscriptionId } = Route.useParams();
  const { data: s, isLoading, isError, error } = useCustomerSubscription(subscriptionId);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [month, setMonth] = useState<Date>(new Date());

  const ledgerMap = useMemo(() => {
    if (!s?.ledger) return new Map<string, any>();
    const map = new Map<string, any>();
    s.ledger.forEach(entry => {
      map.set(entry.delivery_date, entry);
    });
    return map;
  }, [s?.ledger]);

  if (isLoading) {
    return (
      <AccountShell title="Loading calendar..." description="Fetching your delivery schedule.">
        <div className="space-y-6">
          <Skeleton className="h-[400px] w-full rounded-xl" />
        </div>
      </AccountShell>
    );
  }

  if (isError || !s) {
    return (
      <AccountShell title="Error" description="Subscription not found or failed to load.">
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-sm text-destructive">
          {subscriptionErrorMessage(error)}
        </div>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/account/subscriptions">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to subscriptions
          </Link>
        </Button>
      </AccountShell>
    );
  }

  const selectedEntry = selectedDate ? ledgerMap.get(format(selectedDate, "yyyy-MM-dd")) : null;
  const nextDelivery = s.ledger
    .filter(l => l.status === "scheduled" && new Date(l.delivery_date) >= new Date())
    .sort((a, b) => a.delivery_date.localeCompare(b.delivery_date))[0];

  const modifiers = {
    delivery: (date: Date) => ledgerMap.has(format(date, "yyyy-MM-dd")),
    withinSubscription: (date: Date) => {
      const start = new Date(s.start_date);
      const end = new Date(s.end_date);
      return isWithinInterval(date, { start, end });
    }
  };

  const modifierStyles = {
    delivery: { fontWeight: 'bold', textDecoration: 'underline' },
  };

  return (
    <AccountShell 
      title="Delivery Calendar" 
      description={`${s.plan_name} • REF: ${s.originating_order_ref || s.id.slice(0, 8)}`}
    >
      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground">
            <Link to="/account/subscriptions/$subscriptionId" params={{ subscriptionId: s.id }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Details
            </Link>
          </Button>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="space-y-6">
            <section className="rounded-2xl border border-border/60 bg-card p-4 sm:p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-primary" />
                  Delivery Schedule
                </h2>
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/30 px-3 py-1.5 rounded-full">
                  <Info className="h-3.5 w-3.5" />
                  <span>Read-only View</span>
                </div>
              </div>

              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  month={month}
                  onMonthChange={setMonth}
                  className="rounded-md border-0 w-full max-w-[500px]"
                  modifiers={modifiers}
                  components={{
                    DayButton: (props) => {
                      const dateStr = format(props.day.date, "yyyy-MM-dd");
                      const entry = ledgerMap.get(dateStr);
                      return (
                        <div className="relative w-full h-full flex flex-col items-center">
                          <button
                            {...props}
                            className={cn(
                              "h-10 w-10 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-full flex items-center justify-center relative",
                              entry && "font-bold text-primary ring-2 ring-primary/20",
                              isSameDay(props.day.date, new Date()) && "bg-secondary text-secondary-foreground"
                            )}
                          >
                            {format(props.day.date, "d")}
                          </button>
                          {entry && (
                            <div 
                              className={cn(
                                "absolute bottom-1 w-1.5 h-1.5 rounded-full",
                                entry.status === 'delivered' ? "bg-emerald-500" : 
                                entry.status === 'scheduled' ? "bg-amber-500" : "bg-muted-foreground"
                              )} 
                            />
                          )}
                        </div>
                      );
                    }
                  }}
                />
              </div>

              <div className="mt-8 pt-6 border-t border-border/60">
                <h3 className="text-sm font-semibold mb-4">Legend</h3>
                <div className="flex flex-wrap gap-4">
                  <LegendItem status="scheduled" label="Scheduled" />
                  <LegendItem status="delivered" label="Delivered" />
                  <LegendItem status="skipped" label="Skipped" />
                  <LegendItem status="rescheduled" label="Rescheduled" />
                  <LegendItem status="cancelled" label="Cancelled" />
                </div>
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <section className="rounded-xl border border-border/60 bg-card p-5 space-y-4 shadow-sm">
              <h3 className="text-sm font-semibold">Delivery Details</h3>
              {selectedDate ? (
                selectedEntry ? (
                  <div className="space-y-4 animate-in fade-in duration-300">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Date</p>
                      <p className="text-sm font-semibold">{formatISTDate(selectedEntry.delivery_date)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Status</p>
                      <DeliveryStatusBadge status={selectedEntry.status} skipReason={selectedEntry.skip_reason} />
                    </div>
                    {selectedEntry.skip_reason && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Note</p>
                        <p className="text-sm text-muted-foreground italic">"{selectedEntry.skip_reason}"</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 space-y-2">
                    <p className="text-sm text-muted-foreground">No delivery scheduled for</p>
                    <p className="text-sm font-medium">{format(selectedDate, "PPP")}</p>
                  </div>
                )
              ) : (
                <p className="text-sm text-muted-foreground italic text-center py-6">Select a date to see details</p>
              )}
            </section>

            {nextDelivery && (
              <section className="rounded-xl bg-primary/5 border border-primary/10 p-5 space-y-3 shadow-sm">
                <h4 className="text-xs font-bold uppercase tracking-wider text-primary/80">Next Delivery</h4>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">{formatISTDate(nextDelivery.delivery_date)}</p>
                    <p className="text-xs text-muted-foreground">Scheduled Delivery</p>
                  </div>
                </div>
              </section>
            )}

            <div className="rounded-xl border border-border/40 bg-muted/30 p-5">
              <p className="text-xs text-muted-foreground leading-relaxed italic">
                Dates within your subscription period ({formatISTDate(s.start_date)} to {formatISTDate(s.end_date)}) are shown on the calendar. Only marked dates have deliveries scheduled.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </AccountShell>
  );
}

function LegendItem({ status, label }: { status: DeliveryStatus, label: string }) {
  const colors: Record<DeliveryStatus, string> = {
    scheduled: "bg-amber-500",
    delivered: "bg-emerald-500",
    skipped: "bg-slate-400",
    rescheduled: "bg-blue-400",
    cancelled: "bg-rose-500"
  };

  return (
    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <div className={cn("h-2 w-2 rounded-full", colors[status])} />
      <span>{label}</span>
    </div>
  );
}

function DeliveryStatusBadge({ status, skipReason }: { status: string, skipReason: string | null }) {
  const config: Record<string, { icon: any, label: string, className: string }> = {
    scheduled: { 
      icon: Clock, 
      label: "Scheduled", 
      className: "text-amber-600 bg-amber-500/10 border-amber-500/20" 
    },
    delivered: { 
      icon: CheckCircle2, 
      label: "Delivered", 
      className: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20" 
    },
    skipped: { 
      icon: SkipForward, 
      label: skipReason ? `Skipped` : "Skipped", 
      className: "text-muted-foreground bg-muted border-border" 
    },
    rescheduled: {
      icon: Clock,
      label: "Rescheduled",
      className: "text-blue-600 bg-blue-500/10 border-blue-500/20"
    },
    cancelled: { 
      icon: XCircle, 
      label: "Cancelled", 
      className: "text-destructive bg-destructive/10 border-destructive/20" 
    }
  };

  const item = config[status.toLowerCase()] || { icon: Clock, label: status, className: "" };
  const Icon = item.icon;

  return (
    <div className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold", item.className)}>
      <Icon className="h-3.5 w-3.5" />
      <span>{item.label}</span>
    </div>
  );
}
